import { Injectable, signal } from "@angular/core";
import { AuthTypes, IVAAPService } from "@int/ivaap-bootstrap";
import { environment } from "../../environments/environment";
import { IvaapAuthStateService } from "./ivaap-auth-state.service";

@Injectable({providedIn: "root"})
export class NGIVAAPService {
    public initialized  = signal(false);
    public IVAAPService: IVAAPService | null = null;
    public isAuthenticated = signal(false);
    private loginInProgress = false;

    constructor(private ivaapAuthState: IvaapAuthStateService) {}


    initialize() {
        if (this.initialized()) {
            console.log("IVAAP is already initialized.");
            return;
        }

        console.log("Initializing IVAAP...");
        this.IVAAPService = new IVAAPService({backendUrl: environment.ivaapBackendUrl, resourcePath: 'resources/'}, AuthTypes.External);
        this.IVAAPService.initialize();
        this.initialized.set(true);

        // Detect if we just returned from IVAAP redirect
        const wasLoginInProgress = sessionStorage.getItem("ivaap_login_in_progress");
        if (wasLoginInProgress === "true") {
            console.log("Detected return from IVAAP redirect - marking as logged in");
            sessionStorage.removeItem("ivaap_login_in_progress");
            this.ivaapAuthState.markLoggedIn();
            this.isAuthenticated.set(true);
            return;
        }

        // Otherwise check stored login state
        if (this.ivaapAuthState.isLoggedIn()) {
            console.log("Found valid stored IVAAP login state");
            this.isAuthenticated.set(true);
        }
    }

    async ensureLogin(): Promise<void> {
        this.initialize();

        // Prevent multiple concurrent login attempts
        if (this.loginInProgress) {
            console.log("IVAAP login already in progress, skipping");
            return;
        }

        // Check if recently authenticated (within session)
        const loginInProgressFlag = sessionStorage.getItem("ivaap_login_in_progress");
        if (loginInProgressFlag === "true") {
            console.log("IVAAP login redirect in progress, skipping");
            // Clear flag after detecting it once to prevent infinite skip
            sessionStorage.removeItem("ivaap_login_in_progress");
            return;
        }

        // If persisted login exists, trust it briefly
        if (this.ivaapAuthState.isLoggedIn()) {
            this.isAuthenticated.set(true);
            console.log("IVAAP already logged in (from storage)");
            return;
        }

        console.log("Starting IVAAP login...");
        // Attempt login
        this.loginInProgress = true;
        sessionStorage.setItem("ivaap_login_in_progress", "true");

        try {
            const loginResult = this.IVAAPService?.authService.login();
            
            // Handle both Promise-based (local) and redirect-based (Azure) login
            if (loginResult && typeof loginResult.then === 'function') {
                // Promise-based login (local dev)
                await loginResult;
                console.log("IVAAP login completed (Promise)");
                sessionStorage.removeItem("ivaap_login_in_progress");
                this.ivaapAuthState.markLoggedIn();
                this.isAuthenticated.set(true);
                this.loginInProgress = false;
            } else {
                // Redirect-based login (Azure) - will redirect away
                console.log("IVAAP login initiated (redirect expected)");
                // Don't clear flags or set state - redirect will happen
                // Flags will be cleared on return via initialize()
            }
        } catch (error) {
            console.error("IVAAP login failed:", error);
            sessionStorage.removeItem("ivaap_login_in_progress");
            this.loginInProgress = false;
            this.markLoggedOut();
            throw error;
        }
    }

    markLoggedOut(): void {
        this.isAuthenticated.set(false);
        this.ivaapAuthState.markLoggedOut();
    }
    
}